import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { callOpenAI } from '../../services/openAIService';
import { parseJsonFromResponse } from '../../utils/jsonValidator';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const ARABIC_NOTE_TRANSLATE_SYSTEM = `You are a medical translator for clinical documentation in Jordan.
Translate the clinical note JSON into Modern Standard Arabic suitable for physicians.

Rules:
- Return ONLY valid JSON with the SAME structure and keys as the input.
- Translate ALL human-readable string values to Arabic (complaints, HPI, PMH, exam, diagnosis, plan, instructions, narrative, etc.).
- Keep JSON keys exactly the same (English keys).
- Keep note_type, ids, booleans, numbers, and empty strings unchanged.
- Translate medication frequency/instructions/duration to Arabic.
- Keep international drug names in Latin script (e.g. metformin, amlodipine) but you may add a common Arabic description in parentheses if helpful.
- Translate vitals labels/values only if they are words; keep numeric vitals as-is.
- Do not add or remove fields.
- Do not invent clinical content.`;

/**
 * Translate doctor note content (note_json, narrative, medications) to Arabic.
 * Labels in the PDF are already Arabic; this makes the clinical text Arabic too.
 */
export async function translateDoctorNoteToArabic(note) {
  const payload = {
    note_type: note?.note_type || null,
    note_json: note?.note_json || {},
    narrative_text: note?.narrative_text || '',
    medications: note?.medications || [],
  };

  const content = await callOpenAI(
    [
      { role: 'system', content: ARABIC_NOTE_TRANSLATE_SYSTEM },
      {
        role: 'user',
        content: `Translate this clinical note to Arabic. Return the full JSON object with the same keys.\n\n${JSON.stringify(payload, null, 2)}`,
      },
    ],
    0.1,
    3500
  );

  const parsed = parseJsonFromResponse(content);
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Failed to translate note to Arabic');
  }

  return {
    ...note,
    note_json: parsed.note_json && typeof parsed.note_json === 'object'
      ? parsed.note_json
      : note.note_json,
    narrative_text:
      typeof parsed.narrative_text === 'string'
        ? parsed.narrative_text
        : note.narrative_text || '',
    medications: Array.isArray(parsed.medications) ? parsed.medications : note.medications || [],
  };
}

const LABELS = {
  en: {
    title: 'Clinical Note',
    patient: 'Patient',
    doctor: 'Doctor',
    date: 'Date',
    noteType: 'Note type',
    types: {
      first_visit: 'First Visit',
      follow_up: 'Follow-up',
      procedure: 'Procedure',
      emergency: 'Emergency',
    },
    subjective: 'Subjective',
    objective: 'Objective',
    assessment: 'Assessment',
    plan: 'Plan',
    chiefComplaint: 'Chief complaint',
    currentComplaints: 'Current complaints',
    hpi: 'History of Present Illness',
    pmh: 'Past Medical History',
    familyHistory: 'Family history',
    socialHistory: 'Social history',
    allergies: 'Allergies',
    conditionStatus: 'Condition status',
    medicationCompliance: 'Medication compliance',
    currentMedications: 'Current medications',
    examination: 'Examination',
    vitals: 'Vitals',
    diagnosis: 'Diagnosis',
    progress: 'Progress',
    medicationsPrescribed: 'Medications prescribed',
    medicationChanges: 'Medication changes',
    investigations: 'Investigations',
    followUp: 'Follow-up',
    instructions: 'Instructions',
    indication: 'Indication',
    procedure: 'Procedure',
    anesthesia: 'Anesthesia',
    findings: 'Findings',
    complications: 'Complications',
    medicationsGiven: 'Medications given',
    postInstructions: 'Post-procedure instructions',
    onset: 'Onset',
    severity: 'Severity',
    symptoms: 'Symptoms',
    immediateTreatment: 'Immediate treatment',
    disposition: 'Disposition',
    referral: 'Referral',
    narrative: 'Narrative',
  },
  ar: {
    title: 'ملاحظة سريرية',
    patient: 'المريض',
    doctor: 'الطبيب',
    date: 'التاريخ',
    noteType: 'نوع الملاحظة',
    types: {
      first_visit: 'زيارة أولى',
      follow_up: 'متابعة',
      procedure: 'إجراء',
      emergency: 'طوارئ',
    },
    subjective: 'القسم الذاتي',
    objective: 'القسم الموضوعي',
    assessment: 'التقييم',
    plan: 'الخطة العلاجية',
    chiefComplaint: 'الشكوى الرئيسية',
    currentComplaints: 'الشكاوى الحالية',
    hpi: 'تاريخ المرض الحالي',
    pmh: 'التاريخ المرضي السابق',
    familyHistory: 'التاريخ العائلي',
    socialHistory: 'التاريخ الاجتماعي',
    allergies: 'الحساسية',
    conditionStatus: 'حالة المرض',
    medicationCompliance: 'الالتزام بالأدوية',
    currentMedications: 'الأدوية الحالية',
    examination: 'الفحص السريري',
    vitals: 'العلامات الحيوية',
    diagnosis: 'التشخيص',
    progress: 'التقدم',
    medicationsPrescribed: 'الأدوية الموصوفة',
    medicationChanges: 'تغييرات الأدوية',
    investigations: 'الفحوصات',
    followUp: 'المتابعة',
    instructions: 'التعليمات',
    indication: 'الاستطباب',
    procedure: 'الإجراء',
    anesthesia: 'التخدير',
    findings: 'النتائج',
    complications: 'المضاعفات',
    medicationsGiven: 'الأدوية المعطاة',
    postInstructions: 'تعليمات ما بعد الإجراء',
    onset: 'بداية الأعراض',
    severity: 'الشدة',
    symptoms: 'الأعراض المرافقة',
    immediateTreatment: 'العلاج الفوري',
    disposition: 'التصرف',
    referral: 'الإحالة',
    narrative: 'السرد السريري',
  },
};

function section(title, body) {
  if (!body) return '';
  return `<h2>${escapeHtml(title)}</h2>${body}`;
}

function field(label, value) {
  if (value == null || value === '') return '';
  return `<p><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</p>`;
}

function medsHtml(label, meds) {
  if (!Array.isArray(meds) || !meds.length) return '';
  const items = meds
    .filter((m) => m && (m.name || m.drug_name))
    .map((m) => {
      const name = m.name || m.drug_name;
      const detail = [m.dose, m.route, m.frequency, m.duration].filter(Boolean).join(' · ');
      return `<li><strong>${escapeHtml(name)}</strong>${detail ? ` — ${escapeHtml(detail)}` : ''}</li>`;
    })
    .join('');
  if (!items) return '';
  return `<p><strong>${escapeHtml(label)}:</strong></p><ul>${items}</ul>`;
}

function vitalsHtml(label, vitals, lang = 'en') {
  if (!vitals || typeof vitals !== 'object') return '';
  const vitalLabels =
    lang === 'ar'
      ? {
          bp: 'ضغط الدم',
          hr: 'نبض',
          pulse: 'نبض',
          temp: 'الحرارة',
          weight: 'الوزن',
          o2_sat: 'تشبع الأكسجين',
          rr: 'معدل التنفس',
          pain: 'الألم',
        }
      : null;
  const entries = Object.entries(vitals).filter(([, v]) => v != null && String(v).trim() !== '');
  if (!entries.length) return '';
  return `<p><strong>${escapeHtml(label)}:</strong> ${escapeHtml(
    entries
      .map(([k, v]) => `${(vitalLabels && vitalLabels[k]) || k}: ${v}`)
      .join(' · ')
  )}</p>`;
}

function buildNoteBody(note, labels, lang = 'en') {
  const noteType = note.note_type || note.noteJson?.note_type;
  const json = note.note_json || note.noteJson || {};
  const narrative = note.narrative_text || '';

  if (narrative && note.note_format === 'narrative') {
    return section(labels.narrative, `<p>${escapeHtml(narrative)}</p>`);
  }

  if (noteType === 'first_visit' || noteType === 'follow_up') {
    const soap = json.soap || {};
    const s = soap.subjective || {};
    const o = soap.objective || {};
    const a = soap.assessment || {};
    const p = soap.plan || {};

    const subjective =
      (noteType === 'first_visit'
        ? field(labels.chiefComplaint, s.chief_complaint) +
          field(labels.hpi, s.history_of_present_illness) +
          field(labels.pmh, s.past_medical_history) +
          field(labels.familyHistory, s.family_history) +
          field(labels.socialHistory, s.social_history) +
          field(labels.allergies, s.allergies)
        : field(labels.currentComplaints, s.current_complaints) +
          field(labels.conditionStatus, s.condition_status) +
          field(labels.medicationCompliance, s.medication_compliance)) +
      medsHtml(labels.currentMedications, s.current_medications);

    const objective =
      vitalsHtml(labels.vitals, o.vitals, lang) +
      field(labels.examination, o.examination_findings);

    const assessment =
      field(labels.diagnosis, a.diagnosis) + field(labels.progress, a.progress);

    const plan =
      medsHtml(
        noteType === 'follow_up' ? labels.medicationChanges : labels.medicationsPrescribed,
        p.medication_changes || p.medications_prescribed || note.medications
      ) +
      field(labels.investigations, p.investigations) +
      field(labels.followUp, p.follow_up) +
      field(labels.instructions, p.instructions);

    return (
      section(labels.subjective, subjective) +
      section(labels.objective, objective) +
      section(labels.assessment, assessment) +
      section(labels.plan, plan)
    );
  }

  if (noteType === 'procedure') {
    return (
      section(labels.indication, field(labels.indication, json.indication)) +
      section(labels.procedure, field(labels.procedure, json.procedure_performed)) +
      section(labels.anesthesia, field(labels.anesthesia, json.anesthesia_used)) +
      section(labels.findings, field(labels.findings, json.findings)) +
      section(labels.complications, field(labels.complications, json.complications || (lang === 'ar' ? 'لا يوجد' : 'None'))) +
      section(
        labels.medicationsGiven,
        medsHtml(labels.medicationsGiven, json.medications_given || note.medications)
      ) +
      section(
        labels.postInstructions,
        field(labels.postInstructions, json.post_procedure_instructions)
      ) +
      section(labels.followUp, field(labels.followUp, json.follow_up))
    );
  }

  return (
    section(labels.chiefComplaint, field(labels.chiefComplaint, json.chief_complaint)) +
    section(
      labels.onset,
      field(labels.onset, json.onset) + field(labels.severity, json.severity)
    ) +
    section(labels.symptoms, field(labels.symptoms, json.associated_symptoms)) +
    section(labels.vitals, vitalsHtml(labels.vitals, json.vitals, lang)) +
    section(labels.examination, field(labels.examination, json.examination_findings)) +
    section(labels.diagnosis, field(labels.diagnosis, json.diagnosis)) +
    section(
      labels.immediateTreatment,
      field(labels.immediateTreatment, json.immediate_treatment)
    ) +
    section(
      labels.medicationsGiven,
      medsHtml(labels.medicationsGiven, json.medications_given || note.medications)
    ) +
    section(labels.disposition, field(labels.disposition, json.disposition)) +
    section(labels.referral, field(labels.referral, json.referral))
  );
}

function buildClinicalNoteHtml(
  note,
  { lang = 'en', doctorName = '', clinicName = '', clinicLogoUrl = '' } = {}
) {
  const labels = LABELS[lang] || LABELS.en;
  const rtl = lang === 'ar';
  const noteType = note.note_type || '';
  const typeLabel = labels.types[noteType] || noteType;
  const patientName = note.patient_name || '';
  const dateStr = note.created_at
    ? new Date(note.created_at).toLocaleDateString(rtl ? 'ar-JO' : 'en-GB')
    : '';
  const brand = clinicName || 'Sawtna';
  const logoHtml =
    clinicLogoUrl && String(clinicLogoUrl).startsWith('data:image/')
      ? `<img class="logo" src="${clinicLogoUrl}" alt="" />`
      : '';

  return `<!DOCTYPE html>
<html lang="${rtl ? 'ar' : 'en'}" dir="${rtl ? 'rtl' : 'ltr'}">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(labels.title)}</title>
  <style>
  * { box-sizing: border-box; }
  body {
    font-family: "Segoe UI", Tahoma, "Noto Naskh Arabic", "Arial", sans-serif;
    direction: ${rtl ? 'rtl' : 'ltr'};
    text-align: ${rtl ? 'right' : 'left'};
    color: #111;
    margin: 0;
    padding: 32px;
    line-height: 1.65;
    width: 794px;
    background: #fff;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 6px;
  }
  .brand .logo {
    width: 56px;
    height: 56px;
    object-fit: contain;
    border-radius: 8px;
  }
  .brand h1 {
    font-size: 22px;
    margin: 0;
    color: #111827;
  }
  h2 { font-size: 15px; margin: 20px 0 8px; color: #1e40af; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  .meta { color: #555; margin: 14px 0 18px; }
  .meta p { margin: 0 0 4px; }
  ul { ${rtl ? 'padding-right' : 'padding-left'}: 18px; margin: 0 0 8px; }
  p { margin: 0 0 8px; }
  </style>
</head>
<body>
  <div class="brand">
    ${logoHtml}
    <h1>${escapeHtml(brand)}</h1>
  </div>
  <div class="meta">
    <p><strong>${escapeHtml(labels.patient)}:</strong> ${escapeHtml(patientName || '—')}</p>
    ${doctorName ? `<p><strong>${escapeHtml(labels.doctor)}:</strong> ${escapeHtml(doctorName)}</p>` : ''}
    <p><strong>${escapeHtml(labels.noteType)}:</strong> ${escapeHtml(typeLabel)}</p>
    ${dateStr ? `<p><strong>${escapeHtml(labels.date)}:</strong> ${escapeHtml(dateStr)}</p>` : ''}
  </div>
  ${buildNoteBody(note, labels, lang) || `<p>${escapeHtml(narrativeFallback(note))}</p>`}
</body>
</html>`;
}

function narrativeFallback(note) {
  return note.narrative_text || '—';
}

function renderOffscreenHtml(html) {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.left = '-10000px';
  iframe.style.top = '0';
  iframe.style.width = '794px';
  iframe.style.height = '1123px';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    throw new Error('Could not render clinical note PDF.');
  }

  doc.open();
  doc.write(html);
  doc.close();

  return { iframe, body: doc.body };
}

/** Download a doctor clinical note PDF in English or Arabic. */
export async function downloadDoctorClinicalNotePdf(
  note,
  { lang = 'en', doctorName = '', clinicName = '', clinicLogoUrl = '' } = {}
) {
  let noteForPdf = note;
  if (lang === 'ar') {
    noteForPdf = await translateDoctorNoteToArabic(note);
  }

  const html = buildClinicalNoteHtml(noteForPdf, {
    lang,
    doctorName,
    clinicName,
    clinicLogoUrl,
  });
  const { iframe, body } = renderOffscreenHtml(html);

  try {
    await new Promise((resolve) => setTimeout(resolve, 150));
    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: 794,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const safeName = String(note.patient_name || 'patient').replace(/[^\w-]+/g, '_');
    const langTag = lang === 'ar' ? 'AR' : 'EN';
    pdf.save(`Sawtna_Note_${langTag}_${safeName}_${Date.now()}.pdf`);
  } finally {
    document.body.removeChild(iframe);
  }
}
